<!--
  Accessibility Widget ("Dostępność")

  Inline header control that lets visitors adjust the page to their needs:
    - A   → normal text / element size (zoom 1.0)
    - A+  → larger (zoom 1.15)
    - A++ → largest (zoom 1.3)
    - A   → high-contrast mode (black background, yellow text), toggled on/off

  State is applied to <html> (CSS var `--a11y-zoom` + class `a11y-contrast`) and
  read by the `.a11y-scope` wrapper in Main.vue, so the page content scales /
  re-colors while the widget control keeps its own styling. Choices persist in
  localStorage across pages.

  Toggle styling matches Figma menu mobile (8029:12848): border only on the
  active size choice, text stays Sky/900 #0C4A6E. Contrast keeps its own colors
  but follows the same border-only-when-active rule.
-->
<template>
  <!-- Inline variant — buttons laid out in a row (mobile drawer, matches Figma) -->
  <div v-if="inline" class="a11y-widget flex items-center gap-[16px]" role="group" :aria-label="t('accessibility.settings')">
    <button
      v-for="size in sizes"
      :key="size.value"
      type="button"
      class="flex h-[40px] min-w-[40px] items-center justify-center rounded-[8px] px-[8px] font-sans font-medium leading-[32px] tracking-[-0.48px] text-code-medical-center-nav transition-colors hover:bg-code-medical-center-nav/5"
      :class="fontScale === size.value ? 'border border-code-medical-center-nav' : 'border border-transparent'"
      :style="{ fontSize: size.fontSize }"
      :aria-pressed="fontScale === size.value"
      :title="size.label"
      :aria-label="size.label"
      @click="setFontScale(size.value)"
    >
      {{ size.text }}
    </button>

    <!-- Contrast control (not in Figma mobile frame; same active-border rule, own colors) -->
    <button
      type="button"
      class="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[8px] bg-black text-[24px] font-sans font-medium leading-none text-[#f5b50a] transition-colors"
      :class="highContrast ? 'border border-[#f5b50a]' : 'border border-transparent'"
      :aria-pressed="highContrast"
      :title="t('accessibility.high_contrast')"
      :aria-label="t('accessibility.high_contrast')"
      @click="toggleContrast"
    >
      A
    </button>
  </div>

  <!-- Dropdown variant (default — desktop header) -->
  <div v-else ref="root" class="a11y-widget relative">
    <!-- Toggle button (matches header placeholder) -->
    <button
      type="button"
      class="border border-code-medical-center-nav flex items-center justify-center overflow-hidden p-[8px] rounded-[8px] w-[48px] h-[48px] cursor-pointer transition-colors hover:bg-code-medical-center-nav/5"
      :class="open ? 'ring-2 ring-code-medical-center-nav/40' : ''"
      :aria-expanded="open"
      aria-haspopup="true"
      :aria-label="t('accessibility.tools')"
      @click="open = !open"
    >
      <span
        class="text-code-medical-center-nav text-[24px] leading-none text-center tracking-[-0.48px] font-sans font-medium"
      >
        A
      </span>
    </button>

    <!-- Dropdown panel -->
    <transition name="a11y-panel">
      <div
        v-if="open"
        class="absolute left-1/2 top-full z-50 mt-2 flex -translate-x-1/2 flex-col items-center gap-[8px] rounded-[12px] border border-code-medical-center-secondary bg-white p-[8px] shadow-lg"
        role="group"
        :aria-label="t('accessibility.settings')"
      >
        <button
          v-for="size in sizes"
          :key="size.value"
          type="button"
          class="flex h-[48px] w-[48px] items-center justify-center rounded-[8px] font-sans font-medium leading-[32px] tracking-[-0.48px] text-code-medical-center-nav transition-colors hover:bg-code-medical-center-nav/5"
          :class="fontScale === size.value ? 'border border-code-medical-center-nav' : 'border border-transparent'"
          :style="{ fontSize: size.fontSize }"
          :aria-pressed="fontScale === size.value"
          :title="size.label"
          :aria-label="size.label"
          @click="setFontScale(size.value)"
        >
          {{ size.text }}
        </button>

        <button
          type="button"
          class="flex h-[48px] w-[48px] items-center justify-center rounded-[8px] bg-black text-[24px] font-sans font-medium leading-none text-[#f5b50a] transition-colors"
          :class="highContrast ? 'border border-[#f5b50a]' : 'border border-transparent'"
          :aria-pressed="highContrast"
          :title="t('accessibility.high_contrast')"
          :aria-label="t('accessibility.high_contrast')"
          @click="toggleContrast"
        >
          A
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

defineProps({
  // `true` renders the controls inline (mobile drawer); default is the
  // collapsible "A" dropdown used in the desktop header.
  inline: {
    type: Boolean,
    default: false,
  },
});

const FONT_SCALE_KEY = 'a11y-font-scale';
const CONTRAST_KEY = 'a11y-contrast';

const ZOOM_BY_SCALE = {
  normal: 1,
  large: 1.15,
  xlarge: 1.3,
};

const sizes = computed(() => [
  { value: 'normal', text: 'A', fontSize: '24px', label: t('accessibility.normal_text') },
  { value: 'large', text: 'A+', fontSize: '24px', label: t('accessibility.larger_text') },
  { value: 'xlarge', text: 'A++', fontSize: '24px', label: t('accessibility.largest_text') },
]);

const root = ref(null);
const open = ref(false);
const fontScale = ref('normal');
const highContrast = ref(false);

const applyFontScale = (value) => {
  const zoom = ZOOM_BY_SCALE[value] ?? 1;
  document.documentElement.style.setProperty('--a11y-zoom', zoom);
};

const applyContrast = (value) => {
  document.documentElement.classList.toggle('a11y-contrast', value);
};

const setFontScale = (value) => {
  fontScale.value = value;
};

const toggleContrast = () => {
  highContrast.value = !highContrast.value;
};

const handleOutsideClick = (event) => {
  if (open.value && root.value && !root.value.contains(event.target)) {
    open.value = false;
  }
};

watch(fontScale, (value) => {
  applyFontScale(value);
  try {
    window.localStorage.setItem(FONT_SCALE_KEY, value);
  } catch {
    /* localStorage unavailable (private mode) — skip persistence */
  }
});

watch(highContrast, (value) => {
  applyContrast(value);
  try {
    window.localStorage.setItem(CONTRAST_KEY, value ? '1' : '0');
  } catch {
    /* localStorage unavailable (private mode) — skip persistence */
  }
});

onMounted(() => {
  let storedScale = null;
  let storedContrast = null;
  try {
    storedScale = window.localStorage.getItem(FONT_SCALE_KEY);
    storedContrast = window.localStorage.getItem(CONTRAST_KEY);
  } catch {
    /* localStorage unavailable — fall back to defaults */
  }

  if (storedScale && storedScale in ZOOM_BY_SCALE) {
    fontScale.value = storedScale;
  }
  highContrast.value = storedContrast === '1';

  applyFontScale(fontScale.value);
  applyContrast(highContrast.value);

  document.addEventListener('click', handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>

<style scoped>
.a11y-panel-enter-active,
.a11y-panel-leave-active {
  transition: opacity 0.18s ease;
}

.a11y-panel-enter-from,
.a11y-panel-leave-to {
  opacity: 0;
}
</style>
